import React from 'react';
import { AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';

import { useActivationChecklist } from '~clients/hooks/useActivationChecklist';

import { Spinner } from '~components';
import Icon, { faCheck, faXmark } from '~components/icon';

import { CarePlan } from '~clients/models';

import ActivatePlanButton from './ActivatePlanButton';

interface ComponentProps {
  plan: CarePlan;
  /** Accordion section id this panel toggles under. */
  targetId: string;
}

/**
 * Sidebar Activation Checklist section — read-only pass/fail items derived from live
 * plan data, with an (X/Y) progress count in the header.
 */
const ActivationChecklistPanel: React.FC<ComponentProps> = ({ plan, targetId }) => {
  const { items, completeCount, total, isLoading } = useActivationChecklist(plan);

  return (
    <AccordionItem>
      <AccordionHeader targetId={targetId}>
        <span>Activation checklist</span>
        {!isLoading && (
          <span className="text-muted ms-2" data-cy="care-plan-checklist-summary">
            {`(${completeCount}/${total})`}
          </span>
        )}
      </AccordionHeader>
      <AccordionBody accordionId={targetId}>
        {isLoading && (
          <div className="care-plan-section-loading">
            <Spinner loading />
          </div>
        )}
        {!isLoading && (
          <ul className="care-plan-checklist-items" data-cy="care-plan-checklist">
            {items.map(({ key, label, passed }) => (
              <li
                key={key}
                className="care-plan-checklist-item"
                data-cy={`care-plan-checklist-item-${key.replace(/_/g, '-')}`}
              >
                <Icon
                  icon={passed ? faCheck : faXmark}
                  className={passed ? 'care-plan-checklist-pass' : 'care-plan-checklist-fail'}
                  fixedWidth
                  aria-hidden
                />
                <span className="visually-hidden">{passed ? 'Complete:' : 'Incomplete:'}</span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        )}
        {/* Activate action lives with the checklist; only a Draft plan can be activated.
            key={plan.id} remounts the button on a plan switch so its mutation error/loading
            state can't bleed from one plan onto another (both stay mounted in the accordion). */}
        {!isLoading && !plan.activated_at && (
          <ActivatePlanButton key={plan.id} plan={plan} canActivate={completeCount === total} />
        )}
      </AccordionBody>
    </AccordionItem>
  );
};

export default ActivationChecklistPanel;
